import { useInfiniteQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useState } from "react";
import { useInView } from "react-intersection-observer";
import debounce from "lodash.debounce";
import Select from "react-select";
import { fetchHrpPractitionerRoleList, fetchPractitionerRole, fetchPractitionersByBenefitNetwork, fetchPractitionersBySupplier, fetchPractitionersBySupplierNetwork } from "../../utils/apiUtils";
import SharedTable from "../benefitPlan/SharedTable";
import { Link, useSearchParams } from "react-router";

const limit = 20;

const searchOptions = [
    { value: "name", label: "Name" },
    { value: "npi", label: "NPI" },
    { value: "practitionerHccId", label: "Practitioner HCC ID" },
];

const pcpOptions = [
    { value: "all", label: "All roles" },
    { value: "pcp", label: "PCP only" },
];

const withQuery = (path, params) => {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
        if (v != null && v !== "") qs.set(k, String(v));
    });
    const s = qs.toString();
    return s ? `${path}?${s}` : path;
};

const FilterChip = ({ name, value, label, onClear }) => (
    <Select
        name={name}
        className="react-select-container large-input"
        classNamePrefix="react-select"
        isClearable
        value={{ value, label }}
        options={[{ value, label }]}
        onChange={(option) => {
            if (!option) onClear();
        }}
    />
);

const flattenRole = (row) => ({
    key: [row.hccId, row.supplier?.hccId, row.location?.hccId, row.role?.name].filter(Boolean).join("-"),
    practitionerHccId: row.hccId,
    practitionerName: row.fullName || [row.firstName, row.lastName].filter(Boolean).join(" "),
    firstName: row.firstName,
    lastName: row.lastName,
    npi: row.npi,
    phone: row.phone,
    roleName: row.role?.name,
    isPcp: row.role?.isPcp ? "Y" : "N",
    isSpecialist: row.role?.isSpecialist ? "Y" : "N",
    specialty: row.role?.specialty?.name,
    roleStatus: row.role?.status,
    supplierName: row.supplier?.name,
    supplierHccId: row.supplier?.hccId,
    locationName: row.location?.name,
    city: row.location?.address?.city,
    state: row.location?.address?.state,
    role: row.role,
    supplier: row.supplier,
    location: row.location,
});

const PractitionerRoles = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const benefitNetworkId = searchParams.get("benefitNetworkId") || "";
    const supplierNetworkHccId = searchParams.get("supplierNetworkHccId") || "";
    const networkName = searchParams.get("networkName") || "";
    const networkFilter = benefitNetworkId || supplierNetworkHccId;
    const practitionerHccId = searchParams.get("practitionerHccId") || "";
    const supplierHccId = searchParams.get("supplierHccId") || "";

    const dropParams = (...keys) => {
        const next = new URLSearchParams(searchParams);
        keys.forEach((k) => next.delete(k));
        setSearchParams(next);
    };

    const [searchValue, setSearchValue] = useState();
    const [decouncedSearchValue, setDecouncedSearchValue] = useState();
    const [searchBy, setSearchBy] = useState(searchOptions[0]);
    const [pcpFilter, setPcpFilter] = useState(pcpOptions[0]);
    const { ref, inView } = useInView();

    const { data, error, fetchNextPage, hasNextPage, isLoading, isFetching, isFetchingNextPage, status } = useInfiniteQuery({
        queryKey: ["practitionerRoles", limit, decouncedSearchValue, searchBy?.value, pcpFilter?.value, benefitNetworkId, supplierNetworkHccId, practitionerHccId, supplierHccId],
        queryFn: async ({ pageParam }) => {
            const page = typeof pageParam === "number" && pageParam > 0 ? pageParam : 1;
            const params = { page, pageSize: limit };
            if (decouncedSearchValue && searchBy?.value) {
                params[searchBy.value] = decouncedSearchValue;
            }
            if (pcpFilter?.value === "pcp") {
                params.isPcp = true;
            }
            if (practitionerHccId && searchBy?.value !== "practitionerHccId") {
                params.practitionerHccId = practitionerHccId;
            }
            if (supplierHccId) {
                params.supplierHccId = supplierHccId;
            }
            const result = benefitNetworkId
                ? await fetchPractitionersByBenefitNetwork({ benefitNetworkId, ...params })
                : supplierNetworkHccId
                    ? await fetchPractitionersBySupplierNetwork({ supplierNetworkHccId, ...params })
                    : supplierHccId
                        ? await fetchPractitionersBySupplier({ supplierHccId, ...params })
                        : await fetchHrpPractitionerRoleList(params);
            return {
                count: Number(result?.meta?.total) || 0,
                rows: (result?.data || []).map(flattenRole),
            };
        },
        select: (data) => {
            return { count: data.pages?.[0]?.count, rows: data.pages.map((page) => page.rows).flatMap((x) => x) };
        },
        initialPageParam: 1,
        getNextPageParam: (lastPage, pages) => {
            if (!lastPage?.rows?.length || lastPage.rows.length < limit) return undefined;
            const loaded = pages.reduce((accum, page) => accum + (page.rows?.length || 0), 0);
            const total = Number(lastPage.count) || 0;
            if (total && loaded >= total) return undefined;
            return pages.length + 1;
        },
        staleTime: 0,
    });

    useEffect(() => {
        if (inView && hasNextPage && !isFetchingNextPage) {
            fetchNextPage();
        }
    }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage]);

    const debouncedChangeHandler = useCallback(
        debounce((event) => {
            setDecouncedSearchValue(event.target.value?.trim());
        }, 500),
        []
    );

    const columns = [
        "practitionerHccId",
        "practitionerName",
        "npi",
        "roleName",
        "isPcp",
        "isSpecialist",
        "specialty",
        "supplierHccId",
        "supplierName",
        "locationName",
        "city",
        "state",
        "roleStatus",
    ];

    const columnLinks = {
        practitionerHccId: (row) => row.practitionerHccId
            ? withQuery("/providers/practitioners", {
                practitionerHccId: row.practitionerHccId,
                benefitNetworkId,
                supplierNetworkHccId,
                networkName,
            })
            : null,
        supplierHccId: (row) => row.supplierHccId
            ? withQuery("/providers/suppliers", {
                supplierHccId: row.supplierHccId,
                benefitNetworkId,
                supplierNetworkHccId,
                networkName,
            })
            : null,
    };

    const onDetailHandler = async (row) => {
        let soap = null;
        if (row.practitionerHccId) {
            try {
                soap = await fetchPractitionerRole({
                    includeTerminated: "true",
                    practitionerHccId: row.practitionerHccId,
                });
            } catch {
                soap = null;
            }
        }
        const soapRoles = soap?.items?.[0]?.practitionerRole ?? soap?.practitionerRole ?? [];
        return {
            role: row.role,
            supplier: row.supplier,
            location: row.location,
            soapRoles: Array.isArray(soapRoles) ? soapRoles : soapRoles ? [soapRoles] : [],
        };
    };

    return (
        <div className={"container "}>
            <p className="title">Roles  ( {data?.count || 0} )</p>
            <p className="sub-title">
                A role is the practitioner at a supplier and location. That is what a claim uses for in-network, not the person alone.{" "}
                <Link to="/providers/guide">How this wires up in HRP</Link>
            </p>

            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "1rem 1rem 1rem 0rem" }}>
                <div style={{ display: "flex", gap: "1rem", alignItems: "center", width: "100%", flexWrap: "wrap" }}>
                    {networkFilter && (
                        <FilterChip
                            name="network"
                            value={networkFilter}
                            label={`${benefitNetworkId ? "Benefit network" : "Supplier network"}: ${networkName || networkFilter}`}
                            onClear={() => dropParams("benefitNetworkId", "supplierNetworkHccId", "networkName")}
                        />
                    )}
                    {practitionerHccId && (
                        <FilterChip
                            name="practitioner"
                            value={practitionerHccId}
                            label={`Practitioner: ${practitionerHccId}`}
                            onClear={() => dropParams("practitionerHccId")}
                        />
                    )}
                    {supplierHccId && (
                        <FilterChip
                            name="supplier"
                            value={supplierHccId}
                            label={`Supplier: ${supplierHccId}`}
                            onClear={() => dropParams("supplierHccId")}
                        />
                    )}
                    <Select
                        name={"searchBy"}
                        className="react-select-container large-input"
                        classNamePrefix="react-select"
                        placeholder={"Search by"}
                        options={searchOptions}
                        value={searchBy}
                        onChange={(option) => { setSearchBy(option); }}
                    />
                    <input
                        style={{ fontSize: "1rem", minWidth: "250px", padding: "1rem", borderRadius: ".4rem", border: "1px solid var(--lighterGrey)", outline: "none" }}
                        type="text"
                        id={"search"}
                        placeholder="search..."
                        autoComplete="off"
                        onChange={(e) => {
                            setSearchValue(e.target.value);
                            debouncedChangeHandler(e);
                        }}
                        value={searchValue}
                    />
                    <Select
                        name={"pcp"}
                        className="react-select-container large-input"
                        classNamePrefix="react-select"
                        placeholder={"Role type"}
                        options={pcpOptions}
                        value={pcpFilter}
                        onChange={(option) => { setPcpFilter(option); }}
                    />
                </div>
            </div>

            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0rem 1rem 1rem 0rem" }}>
                <p
                    style={{ cursor: "pointer", color: "var(--red)" }}
                    onClick={() => {
                        setDecouncedSearchValue("");
                        setSearchValue("");
                        setSearchBy(searchOptions[0]);
                        setPcpFilter(pcpOptions[0]);
                        setSearchParams({});
                    }}
                >
                    Clear filters
                </p>
            </div>

            {status === "loading" || status === "pending" ? (
                <p>Loading...</p>
            ) : status === "error" ? (
                <span className="danger">Error: {error.message}</span>
            ) : (
                <SharedTable
                    type={"practitioner-roles"}
                    columns={columns}
                    items={data?.rows}
                    activeDraftId={null}
                    hasNextPage={hasNextPage}
                    isFetchingNextPage={isFetchingNextPage}
                    fetchNextPage={fetchNextPage}
                    isFetching={isFetching}
                    isLoading={isLoading}
                    searchText={decouncedSearchValue}
                    onDetailHandler={onDetailHandler}
                    columnLinks={columnLinks}
                    ref={ref}
                />
            )}
        </div>
    );
};

export default PractitionerRoles;
